/* NTL — INFINITE SCROLL
   Turns the theme's "Show more" buttons into infinite scroll without rewriting a
   line of the loading code.

   WHY IT ONLY CLICKS
   There are two load implementations here — snippets/ntl-show-more.liquid for the
   blog, search, article comments and account orders, and a second one inside
   sections/ntl-collection-grid.liquid for the grid. Both are delegated click
   listeners that fetch the next page, move the children across, carry the next
   URL onto the same button and retire it at the end. Re-implementing that twice
   more would mean four copies of the same fetch to keep in step. This watches for
   whichever button is on the page and clicks it, so every fix to either loader is
   inherited for free, and turning the feature off leaves the original behaviour
   untouched.

   WHAT IT GUARDS AGAINST
     · a second load starting while one is in flight        -> `disabled` is the lock
     · the button still being on screen after the append    -> that is the point,
       but each pass is gated on the button being idle again, so passes cannot stack
     · the list being re-rendered by a filter, which replaces the button with a new
       element                                              -> a MutationObserver rescans
     · loading the whole collection into one page, which puts the footer out of
       reach                                                -> a cap, after which the
       button comes back and stays
     · the appended page not pushing the button out of view -> IntersectionObserver
       fires on threshold CROSSINGS only, so a button that never stops intersecting
       never reports again and the list quietly stalls; each pass re-arms it
     · a failed fetch, after which the loader re-enables the button in place ->
       re-arming would retry forever, so a pass that did not advance the next-page
       URL stops auto-loading and leaves the button for the reader
     · the loader rewriting the address to ?page=N on every pass -> that is right
       for a click and wrong for a scroll, because reloading or coming Back would
       then open halfway down a list the reader never asked for; the address is put
       back after each automatic pass
     · a list with a form under it (article comments) -> opted out per surface, since
       auto-loading walks the "Leave a comment" form further away with every pass
     · no IntersectionObserver                              -> nothing happens and the
       button behaves exactly as it did before

   The button is never removed and never hidden from assistive technology: with the
   list auto-loading it is the only control a keyboard user has, and it is what a
   crawler follows. It is visually hidden only while auto-loading is still armed.
*/
(function () {
  'use strict';
  if (window.__ntlInfinite) return;
  window.__ntlInfinite = true;

  var SEL = '[data-ntl-more],[data-ntl-cg-more]';
  var body = document.body;
  if (!('IntersectionObserver' in window)) return;

  /* PER BUTTON, not per page. Each section resolves its own Pagination setting
     against the theme default and stamps the answer on its button, so one list
     can scroll on while another keeps its button — which is exactly what the
     section-level override is for. The body attributes remain as the fallback
     for a button rendered before this attribute existed. */
  function modeOf(btn) {
    return btn.getAttribute('data-ntl-more-mode') ||
           (body && body.getAttribute('data-ntl-pagination')) || 'more';
  }
  function capOf(btn) {
    if (modeOf(btn) !== 'hybrid') return 0;      // 0 = no cap
    var n = parseInt(btn.getAttribute('data-ntl-more-max') ||
                     (body && body.getAttribute('data-ntl-infinite-max')) || '3', 10);
    return n > 0 ? n : 3;
  }
  /* Counted PER BUTTON, not once for the page. Changing a facet replaces the grid
     and with it the button, and a shared counter would arrive at the new list
     already spent — the reader would filter, scroll, and get nothing. */
  var loads = new WeakMap();
  var seen = new WeakSet();
  var stopped = new WeakSet();                    // buttons whose last pass failed
  var io;

  function status(btn) {
    var w = btn.parentElement;
    if (!w) return null;
    var live = w.querySelector('[data-ntl-live]');
    if (!live) {
      live = document.createElement('span');
      live.setAttribute('data-ntl-live', '');
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      live.className = 'ntl-visually-hidden';
      w.appendChild(live);
    }
    return live;
  }

  /* Visually hidden, NOT display:none — a button that is not in the accessibility
     tree cannot be reached by keyboard, and with the page auto-loading it is the
     only way to page through without a mouse. */
  function arm(btn) {
    var w = btn.parentElement;
    if (w) w.classList.add('ntl-more-auto');
  }
  function disarm(btn) {
    var w = btn.parentElement;
    if (w) w.classList.remove('ntl-more-auto');
  }

  function nextUrl(btn) {
    return btn.getAttribute('data-ntl-more-url') || btn.getAttribute('data-ntl-cg-more-url') || '';
  }

  function pull(btn) {
    if (btn.disabled) return;                     // a load is already running
    if (stopped.has(btn)) return;
    var cap = capOf(btn);
    var done = loads.get(btn) || 0;
    if (cap && done >= cap) { disarm(btn); return; }
    var was = nextUrl(btn);
    var addr = location.href;                     // put this back afterwards
    loads.set(btn, done + 1);
    var live = status(btn);
    if (live) live.textContent = btn.getAttribute('data-ntl-more-busy') ||
                                 btn.getAttribute('data-ntl-cg-more-busy') || 'Loading more';
    btn.click();

    /* The loaders re-enable the button when the next page is ready, or remove its
       wrapper when there is none. Watch the button itself rather than guessing a
       delay: a slow network would otherwise be read as "finished". */
    var t0 = Date.now();
    var tick = setInterval(function () {
      if (!btn.isConnected) {
        /* The last page: the loader removed the button's wrapper. The address
           still has to go back, or the reader ends up on ?page=5 — the one case
           the restore below never reaches, because there is no button left to
           watch. */
        clearInterval(tick);
        if (live && live.isConnected) live.textContent = '';
        if (window.history && history.replaceState) {
          try { history.replaceState(history.state, '', addr); } catch (err) {}
        }
        rescan();
        return;
      }
      if (!btn.disabled) {
        clearInterval(tick);
        /* The loader restores the same label and the same enabled state whether it
           succeeded or failed. What tells them apart is the next-page URL: on
           success the button now points at the page after this one. */
        var moved = nextUrl(btn) !== was;
        if (live) live.textContent = moved ? 'More loaded' : '';
        if (!moved) { stopped.add(btn); disarm(btn); return; }

        /* Right for a click, wrong for a scroll: reloading or coming Back would
           open partway down a list nobody asked for. */
        if (window.history && history.replaceState) {
          try { history.replaceState(history.state, '', addr); } catch (err) {}
        }

        if (cap && (loads.get(btn) || 0) >= cap) { disarm(btn); return; }
        /* Re-arm. IntersectionObserver reports threshold CROSSINGS, so a button
           still on screen after the append never reports again on its own. */
        io.unobserve(btn);
        io.observe(btn);
        rescan();
      } else if (Date.now() - t0 > 20000) {
        clearInterval(tick);                      // gave up; the button is the fallback
        disarm(btn);
      }
    }, 120);
  }

  function watch(btn) {
    if (seen.has(btn)) return;
    /* A list with a form or a next step under it is left alone: every automatic
       pass would push that further down the page. */
    if (btn.closest('[data-ntl-no-infinite]')) return;
    var m = modeOf(btn);
    if (m !== 'infinite' && m !== 'hybrid') return;   // this list keeps its button
    seen.add(btn);
    var cap = capOf(btn);
    if (!cap || (loads.get(btn) || 0) < cap) arm(btn);
    io.observe(btn);
  }

  function rescan() {
    var list = document.querySelectorAll(SEL);
    for (var i = 0; i < list.length; i++) watch(list[i]);
  }

  io = new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      if (!en.isIntersecting) return;
      if (!en.target.isConnected) { io.unobserve(en.target); return; }
      pull(en.target);
    });
  }, { rootMargin: '600px 0px' });                // start early enough to feel seamless

  /* A filter or the theme editor replaces the whole list, and with it the button.
     The new one is a different element, so it has to be picked up again. */
  new MutationObserver(function () { rescan(); })
    .observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', rescan);
  } else {
    rescan();
  }
  document.addEventListener('shopify:section:load', rescan);
})();
